import {
  Button,
  Icons,
  MarketingBanner,
  MarketingBannerProps,
  MetricsBox,
  Stack,
  Typography,
  marketingBannerClassNames,
  mergeSx,
  tokens,
} from '@cast/design-system';
import { useNavigate } from '@cast/infrastructure-organization';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { dashesOrNum, formatCurrency, formatPercentage } from '@cast/utils';

import { useInventoryWorkloadsAutoscalerBannerData } from './_hooks/useInventoryWorkloadsAutoscalerBannerData';

type InventoryWorkloadsAutoscalerBannerProps = Pick<
  MarketingBannerProps,
  'closeAnimation' | 'onClose' | 'sx'
>;

const formatResourcePair = (cpu: number, memoryGibs: number) =>
  `${dashesOrNum({ value: cpu, roundBy: 2 })} CPU / ${dashesOrNum({
    value: memoryGibs,
    roundBy: 2,
  })} GiB`;

const formatMonthlyAmount = (amount: number, suffix: string) =>
  `${formatCurrency(amount, { maximumFractionDigits: 2 })}/mo ${suffix}`;

const BannerMetric = ({
  label,
  cpu,
  memoryGibs,
  footer,
  testId,
}: {
  label: string;
  cpu: number;
  memoryGibs: number;
  footer: string;
  testId: string;
}) => (
  <MetricsBox
    testId={testId}
    size="m"
    slots={{
      label,
      value: formatResourcePair(cpu, memoryGibs),
      footer: (
        <Typography
          variant="P12M"
          color={tokens.theme.fg.structural.primaryOnInverse}
        >
          {footer}
        </Typography>
      ),
    }}
    slotProps={{
      label: {
        color: tokens.theme.fg.structural.secondaryOnInverse,
      },
      value: {
        variant: 'metricmd',
        color: tokens.theme.fg.structural.primaryOnInverse,
        whiteSpace: 'nowrap',
      },
    }}
  />
);

/**
 * Single marketing banner for the workloads dashboard.
 * Mount inside `MarketingBannersStack` at the call site.
 */
export const InventoryWorkloadsAutoscalerBanner = ({
  closeAnimation,
  onClose,
  sx,
}: InventoryWorkloadsAutoscalerBannerProps = {}) => {
  const { cluster } = useCluster();
  const navigate = useNavigate();
  const { data, isLoading, hasError } =
    useInventoryWorkloadsAutoscalerBannerData();

  if (isLoading || hasError || !data) {
    return null;
  }

  return (
    <MarketingBanner
      closeAnimation={closeAnimation}
      onClose={onClose}
      testId="inventory-workloads-autoscaler-banner"
      title={`${formatPercentage(data.percentage, {
        roundBy: 1,
      })} of inventory autoscaled`}
      message={`${data.optimizedCount} / ${data.totalCount} cluster workloads`}
      action={
        <Button
          size="sm"
          variant="primary"
          endIcon={<Icons.ArrowRight />}
          onClick={() =>
            navigate(`/automation/external-clusters/${cluster.id}/settings`)
          }
        >
          See Automation Overview
        </Button>
      }
      endContent={
        <Stack
          direction="row"
          gap={24}
          height="100%"
          alignItems="flex-start"
          justifyContent="flex-end"
        >
          <BannerMetric
            testId="inventory-autoscaler-banner-reclaimed"
            label="reclaimed"
            cpu={data.reclaimedCpuCores}
            memoryGibs={data.reclaimedMemoryGibs}
            footer={formatMonthlyAmount(data.reclaimedMonthlySavings, 'saved')}
          />
          <BannerMetric
            testId="inventory-autoscaler-banner-potential"
            label="potential"
            cpu={data.potentialCpuCores}
            memoryGibs={data.potentialMemoryGibs}
            footer={formatMonthlyAmount(
              data.potentialMonthlySavings,
              'available'
            )}
          />
        </Stack>
      }
      sx={mergeSx(
        {
          [`& .${marketingBannerClassNames.endContent}`]: {
            width: 'auto',
          },
        },
        sx
      )}
    />
  );
};
